import styled from 'styled-components'
import { TdsButton, TdsIcon, TdsTooltip } from '@scania/tegel-react'
import { MenuContentNonColour } from './MenuContentNonColour'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MenuContentColour } from './MenuContentColour'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { BreakpointWidthPx } from '../../../css/BreakpointWidthPx'
import {
  getGuidedViewFirstTimeAllView,
  getGuidedViewLoadTime,
  getGuidedViewPage,
  getSeriesFrame,
  getSessionInitDataState,
  setGuidedViewFirstTimeAllView,
  setSeriesFrame,
  setSummaryOrRfqOpenedFromPage,
} from '../../../store/sessionDataSlice'
import { useClient } from '../../../utils/useClient'
import {
  getMenuState,
  setMenuIdToOpenAtJumpToAllView,
  setMenuTreeGv,
} from '../../../store/menuSlice'
import { TextId, useTextsV2 } from '../../../utils/useTexts'
import { getTruckInfoState } from '../../../store/truckInfoSlice'
import { SvgChevronLeft, SvgChevronRight } from '../../../components/SvgImports'
import { useResizeObserver } from '../../../utils/useResizeObserver'
import { Size2d } from '../../../types/Size2d'
import { BreakpointHeightPx } from '../../../css/BreakpointHeightPx'
import {
  pushAdobeEvent,
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingInlineMenuArrowClick,
  ScaniaAdobeTrackingMenuClickEvent,
  ScaniaAdobeTrackingTimeSpentInGuidedViewEvent,
} from '../../../utils/adobeAnalytics'
import {
  getUrlCidBevSemiliveParameters,
  UrlParamMode,
} from '../../../utils/getUrlCidParameter'
import { useRfqEysButtonState } from '../RfqEysButtonState'
import { useNavigate } from 'react-router-dom'
import {
  buildRelativeAllViewPage,
  buildRelativeSummaryPage,
} from '../../../utils/UrlBuilders'
import { getUrlParametersToString } from '../../../utils/getUrlParametersToString'
import { getMarketLanguageState } from '../../../store/appSlice'
import { useDrag } from '@use-gesture/react'
import { TestElementTypeId } from '../../../types/TestAttributeId'
import { GuidedViewMenuNode } from '../../../api/generated'
import { getGvOpenMenuEtelFamily } from '../../../utils/getGvOpenMenuEtelFamily'

interface MenuSectionRootProps {
  $pageIsReady: boolean
}

const MenuSectionRoot = styled.section<MenuSectionRootProps>`
  width: 33%;
  display: flex;
  flex-direction: column;
  padding-right: 24px;
  visibility: ${(props) => (props.$pageIsReady ? '' : 'hidden')};

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    padding-right: 16px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    width: 100%;
    height: unset;
    padding-right: 0px;
  }
`

const ViewAllWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: none;
  }
`

const MenuHeader = styled.div`
  font-size: 18px;
  font-weight: bold;
`

const InlineMenuWrapper = styled.div`
  display: flex;
  margin-bottom: 16px;
  touch-action: pan-y;
  //-moz-user-select: none;
  //-webkit-user-drag: none;
  //user-select: none;

  // Firefox seems to need this as well.
  //pointer-events: none;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    margin-bottom: 4px;
  }
`

const TabsWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 68px;
  align-items: center;
  border-bottom: 1px solid var(--tds-grey-300);
  overflow-x: hidden;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    height: 48px;
  }
`

interface TabsButtonProps {
  $isSelected: boolean
}

const TabsButton = styled.div<TabsButtonProps>`
  padding-left: 16px;
  padding-right: 16px;
  width: fit-content;
  height: 100%;
  white-space: nowrap;
  color: ${(props) =>
    props.$isSelected
      ? 'var(--tds-inline-tabs-tab-color-selected)'
      : 'var(--tds-inline-tabs-tab-color)'};
  font-weight: bold;
  border-bottom: ${(props) =>
    props.$isSelected
      ? '2px solid var(--tds-blue-400)'
      : '2px solid transparent'};
  display: flex;
  align-items: center;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
      border-bottom: ${(props) =>
        props.$isSelected
          ? '2px solid var(--tds-blue-400)'
          : '2px solid var(--tds-grey-400)'};
    }
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    font-size: 13px;
  }
`

interface TabsScrollButtonProps {
  $disabled: boolean
}

const TabsScrollButton = styled.div<TabsScrollButtonProps>`
  min-width: 48px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid var(--tds-grey-300);

  svg {
    height: 20px;
    color: var(--tds-blue-900);
    color: ${(props) =>
      props.$disabled ? 'var(--tds-grey-500)' : 'var(--tds-blue-900)'};
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: ${(props) => (props.$disabled ? 'auto' : 'pointer')};
      background-color: var(--tds-grey-300);
    }
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    border-bottom: 1px solid var(--tds-grey-300);
  }
`

const ButtonSection = styled.section`
  height: auto;
  gap: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-wrap: wrap;
  margin-top: 12px;
  margin-bottom: 12px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px) {
    flex-direction: column;
    height: auto;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: none;
  }
`

const ButtonWrapper = styled.div`
  min-width: 200px;
  flex: 1 1 calc(50% - 8px);

  @media screen and (max-width: ${BreakpointWidthPx.Large}px) {
    min-width: 100%;
    width: 100%;
  }
`

interface MenuSectionProps {
  handleRfqOrEysClick: () => void
  handleItemSelection: (id: string, text: string) => void
  parentSize: Size2d
  highlightedId: string | null
}

export function MenuSection({
  handleRfqOrEysClick,
  handleItemSelection,
  parentSize,
  highlightedId,
}: MenuSectionProps): JSX.Element {
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const client = useClient()
  const dispatch = useAppDispatch()
  const t2 = useTextsV2()
  const navigate = useNavigate()
  const menuTreeGv = useAppSelector(getMenuState).menuResultGv?.menuTree
  const seriesFrame = useAppSelector(getSeriesFrame)
  const truckInfo = useAppSelector(getTruckInfoState)
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const tabsWrapperRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [inlineMenuisOverflowing, setInlineMenuIsOverflowing] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const size = useResizeObserver<Size2d>(rootRef, { width: 0, height: 0 })
  const [pageReady, setPageReady] = useState<boolean>(false)
  const menuInititated = useRef(false)
  const firstTimeVisitingAllView = useAppSelector(getGuidedViewFirstTimeAllView)
  const guidedViewLoadTime = useAppSelector(getGuidedViewLoadTime)
  const urlCidParams = getUrlCidBevSemiliveParameters(UrlParamMode.ExcludeBev)
  const showFpcCodeOnHover = urlCidParams.has('fpc')
  const { rfqOrEysButtonHidden, rfqOrEysButtonText, rfqOrEysButtonEnabled } =
    useRfqEysButtonState()
  const guidedViewPage = useAppSelector(getGuidedViewPage)
  const { currentlyLoadingId } = useAppSelector(getMenuState)
  const interiorMenuId = 'MENU_GV_CAB_INTERIOR'

  const updateImageView = useCallback(
    (submenu: GuidedViewMenuNode[] | null | undefined) => {
      if (
        submenu &&
        submenu?.find((menu) => menu.id === interiorMenuId)?.isOpen
      ) {
        if (seriesFrame.seriesId !== 'INTERIOR') {
          dispatch(setSeriesFrame({ seriesId: 'INTERIOR', frameId: 1 }))
        }
      } else if (seriesFrame.seriesId === 'INTERIOR') {
        dispatch(setSeriesFrame({ seriesId: 'EXTERIOR', frameId: 2 }))
      }
    },
    [dispatch, interiorMenuId, seriesFrame],
  )

  // Inititate menu
  useEffect(() => {
    if (menuInititated.current) {
      return
    }
    if (!sessionInitData?.sessionId) {
      return
    }
    if (!client) {
      return
    }
    if (menuTreeGv) {
      if (!pageReady) {
        updateImageView(menuTreeGv.subMenus)
      }
      setPageReady(true)
      return
    }
    const asyncWrapper = async () => {
      menuInititated.current = true
      var menuId = null
      const newMenuState = await client.exploreMenu({
        sessionId: sessionInitData.sessionId,
        menuId: menuId,
        showAllConfigMenu: false,
      })
      if (newMenuState.success) {
        dispatch(setMenuTreeGv(newMenuState.success))
        setPageReady(true)
        updateImageView(newMenuState.success.menuTree.subMenus)
      }
    }
    asyncWrapper()
  }, [
    client,
    sessionInitData,
    dispatch,
    truckInfo,
    menuTreeGv,
    updateImageView,
    pageReady,
  ])

  const handleInlineTabClick = (menuId: string, text: string) => {
    if (!sessionInitData?.sessionId) {
      return
    }
    if (!client) {
      return
    }

    const asyncWrapper = async () => {
      const newMenuState = await client.exploreMenu({
        sessionId: sessionInitData.sessionId,
        menuId: menuId,
        showAllConfigMenu: false,
      })
      if (newMenuState.success) {
        dispatch(setMenuTreeGv(newMenuState.success))
        updateImageView(newMenuState.success.menuTree?.subMenus)

        const trackingEvent: ScaniaAdobeTrackingMenuClickEvent = {
          event: ScaniaAdobeEventId.MenuClick,
          eventInfo: {
            optionClickedId: menuId,
            optionClickedText: text,
          },
        }
        pushAdobeEvent(trackingEvent)
      }
    }
    asyncWrapper()
  }

  const handleSummaryClick = () => {
    if (!marketLanguage) {
      throw new Error('Expected market language.')
    }
    dispatch(setSummaryOrRfqOpenedFromPage(guidedViewPage))
    const relativeUrl = buildRelativeSummaryPage(marketLanguage)
    const params = relativeUrl.params
    const newUrl = relativeUrl.path + getUrlParametersToString(params)
    navigate(newUrl)
  }

  // Scrolls the inlineTabs menu left or right
  const handleScrollTabsClick = (direction: 'left' | 'right') => {
    if (tabsWrapperRef.current) {
      const scrollAmount = 100
      const newScrollLeft =
        direction === 'left'
          ? tabsWrapperRef.current.scrollLeft - scrollAmount
          : tabsWrapperRef.current.scrollLeft + scrollAmount
      tabsWrapperRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'instant',
      })
      updateScrollState()

      const trackingEvent: ScaniaAdobeTrackingInlineMenuArrowClick = {
        event: ScaniaAdobeEventId.InlineMenuArrowClick,
        eventInfo: {
          direction: direction,
        },
      }
      pushAdobeEvent(trackingEvent)
    }
  }

  // Update scroll state of inline tabs
  useEffect(() => {
    updateScrollState()
  }, [size, tabsWrapperRef, menuTreeGv])

  const updateScrollState = () => {
    if (tabsWrapperRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsWrapperRef.current
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(Math.round(scrollLeft) < scrollWidth - clientWidth - 1)
      setInlineMenuIsOverflowing(scrollWidth > clientWidth)
    }
  }

  const handleViewAllClick = () => {
    if (currentlyLoadingId) {
      console.log(
        'Ignored a button click while waiting for a pending config change.',
      )
      return
    }
    if (!marketLanguage) {
      throw new Error('Expected market language.')
    }
    const relativeUrl = buildRelativeAllViewPage(marketLanguage)
    const params = relativeUrl.params
    const newUrl = relativeUrl.path + getUrlParametersToString(params)
    navigate(newUrl)
    const menuIdToOpenAtJumpToAllView = getGvOpenMenuEtelFamily(menuTreeGv)
    dispatch(setMenuIdToOpenAtJumpToAllView(menuIdToOpenAtJumpToAllView))

    if (firstTimeVisitingAllView && guidedViewLoadTime) {
      const currentTime = Date.now()
      const trackingEvent: ScaniaAdobeTrackingTimeSpentInGuidedViewEvent = {
        event: ScaniaAdobeEventId.GuidedViewTimeSeconds,
        eventInfo: {
          timeSpentUntilAllView: (currentTime - guidedViewLoadTime) / 1000,
        },
      }
      pushAdobeEvent(trackingEvent)
      dispatch(setGuidedViewFirstTimeAllView(false))
    }
  }

  const bindDrag = useDrag(
    (drag) => {
      const swipeThreshold = 0.1
      if (tabsWrapperRef.current && drag.velocity[0] > swipeThreshold) {
        const newScrollLeft =
          tabsWrapperRef.current.scrollLeft - drag.movement[0]
        tabsWrapperRef.current.scrollTo({
          left: newScrollLeft,
          behavior: 'instant',
        })
        updateScrollState()
      }
    },
    {
      axis: 'x',
    },
  )

  return (
    <MenuSectionRoot ref={rootRef} $pageIsReady={pageReady}>
      <ViewAllWrapper>
        <MenuHeader>{t2(TextId.HEADER_GV_PRODUCT_CATEGORIES)}</MenuHeader>
        <TdsTooltip
          selector="#viewAllOrLessButton"
          text={t2(TextId.DESCRIPTION_VIEW_ALL)}
        />
        <TdsButton
          id="viewAllOrLessButton"
          variant="ghost"
          text={t2(TextId.ACTION_LABEL_VIEW_ALL_OPTIONS)}
          size="sm"
          onClick={handleViewAllClick}
          data-test-element-type={TestElementTypeId.GuidedViewViewAllButton}
        >
          <TdsIcon slot="icon" size="16px" name="filters" />
        </TdsButton>
      </ViewAllWrapper>

      <InlineMenuWrapper {...bindDrag()}>
        {inlineMenuisOverflowing && (
          <TabsScrollButton
            onClick={() => handleScrollTabsClick('left')}
            $disabled={!canScrollLeft}
            key={'left' + canScrollLeft.toString()}
            data-test-element-type={
              TestElementTypeId.GuidedViewTabsScrollLeftButton
            }
          >
            <SvgChevronLeft />
          </TabsScrollButton>
        )}
        <TabsWrapper ref={tabsWrapperRef}>
          {menuTreeGv?.subMenus &&
            menuTreeGv.subMenus.map((menuItem, i) => {
              return (
                <TabsButton
                  onClick={() =>
                    handleInlineTabClick(menuItem.id, menuItem.shortText || '')
                  }
                  key={menuItem.id + 'guidedView'}
                  $isSelected={menuItem.isOpen}
                  id={'inlineMenu' + menuItem.id}
                  data-test-element-type={
                    TestElementTypeId.GuidedViewTabsButton
                  }
                  data-test-etel-variant-id={menuItem.id}
                >
                  {showFpcCodeOnHover && (
                    <TdsTooltip
                      placement="bottom"
                      selector={'#inlineMenu' + menuItem.id}
                      text={menuItem.id}
                    />
                  )}
                  {i + 1 + '.  ' + (menuItem.shortText || menuItem.id)}
                </TabsButton>
              )
            })}
        </TabsWrapper>
        {inlineMenuisOverflowing && (
          <TabsScrollButton
            onClick={() => handleScrollTabsClick('right')}
            $disabled={!canScrollRight}
            key={'right' + canScrollRight.toString()}
            data-test-element-type={
              TestElementTypeId.GuidedViewTabsScrollRightButton
            }
          >
            <SvgChevronRight />
          </TabsScrollButton>
        )}
      </InlineMenuWrapper>
      {menuTreeGv?.subMenus?.find((menuItem) => menuItem.id === 'MENU_GV_COLOR')
        ?.isOpen && (
        <MenuContentColour
          parentSize={parentSize}
          handleItemSelection={handleItemSelection}
          highlightedId={highlightedId}
        />
      )}
      {!menuTreeGv?.subMenus?.find(
        (menuItem) => menuItem.id === 'MENU_GV_COLOR',
      )?.isOpen && (
        <MenuContentNonColour
          parentSize={parentSize}
          handleItemSelection={handleItemSelection}
          highlightedId={highlightedId}
        />
      )}
      <ButtonSection>
        <ButtonWrapper>
          {!rfqOrEysButtonHidden && (
            <TdsButton
              variant="primary"
              text={rfqOrEysButtonText}
              size={
                parentSize.width < BreakpointWidthPx.Large ||
                parentSize.height + 64 < BreakpointHeightPx.Large
                  ? 'sm'
                  : 'lg'
              }
              fullbleed
              onClick={() => handleRfqOrEysClick()}
              disabled={!rfqOrEysButtonEnabled}
              data-test-element-type={
                TestElementTypeId.GuidedViewGetConsultationButton
              }
            >
              <TdsIcon slot="icon" size="20px" name="message" />
            </TdsButton>
          )}
        </ButtonWrapper>
        <ButtonWrapper>
          <TdsButton
            variant="secondary"
            text={t2(TextId.LABEL_ACTION_SUMMARY)}
            size={
              parentSize.width < BreakpointWidthPx.Large ||
              parentSize.height + 64 < BreakpointHeightPx.Large
                ? 'sm'
                : 'lg'
            }
            fullbleed
            onClick={handleSummaryClick}
            data-test-element-type={TestElementTypeId.GuidedViewSummaryButton}
          >
            <TdsIcon slot="icon" size="20px" name="document" />
          </TdsButton>
        </ButtonWrapper>
      </ButtonSection>
    </MenuSectionRoot>
  )
}
